import {useMutation, useQuery} from '@tanstack/react-query';
import {
  GetReProductLocationViewRequestParam,
  GetReProductLocationViewResponse,
  SaveReProductionLocationRequestBody,
  SaveReProductionLocationResponse,
  SaveReProductionBasicInfoPayload,
  GetReProductionBasicInfoRequestParam,
  GetReProductionBasicInfoResponse,
  EditViewReProductionBasicInfoResponse,
  SaveReProductionBasicInfoResponse,
  GetPriorProductionListRequestParam,
  GetPriorProductionListResponse,
  SavePriorProductionToReProduceRequestParam,
  SavePriorProductionToReProduceResponse,
  GetReProductionExecuteGenerateRequestBody,
  GetReProductionExecutePlaceholderingRequestBody,
  GetReProductionGenerateViewRequestParam,
  GetReProductionGenerateViewResponse,
  getReProductionGenerateStatusParams,
  GetReProductionGenerateStatusResponse,
  GetReProductionExportBatesRangeRequestParam,
  ReProductionNumberingRequestParam,
  ReProductionNumberingResponse,
  SaveReProductionNumberingRequestParam,
  SaveReProductionNumberingResponse,
  ReProductionDocumentsCountRequest,
  ReProductionDocumentsCountResponse,
} from '../../api/types/reproduction.type';

import {
  getReProductLocationView,
  saveReProductionLocation,
  getReProductionBasicInfoInEditMode,
  getReProductionBasicInfo,
  saveReProductionBasicInfo,
  getPriorProductionsList,
  savePriorProductionToReProduce,
  reProductionExecuteGenerate,
  getReProductionGenerateView,
  reProductionExecutePlaceholdering,
  getReProductionGenerateStatus,
  getReProductionExportBatesRange,
  getReProductionDocumentsCount,
  getReProductionDocumentSelectionView,
  getReProductionNumbering,
  SaveReProductionDocumentSelection,
  saveReProductionNumbering,
  saveReProductionComponents,
  getReProductionComponents,
  getDatComponentReproduction,
  getNativeComponentReproduction,
  getTextComponentReproduction,
  getTiffComponentReproduction,
} from '../../api/endpoints/productionApi';
import {
  GetDocumentSelectionViewRequestParam,
  GetDocumentSelectionViewResponse,
  SaveDocumentsSelectionResponse,
  SaveDocumentsSelectionPayload,
  SaveProductionComponentsResponse,
  SaveProductionComponentsPayload,
  GetProductionComponentsResponse,
  GetProductionComponentsRequestParam,
  GetDatComponentRequestParam,
  GetDatComponentResponse,
  GetNativeComponentRequestParam,
  GetNativeComponentResponse,
  GetTextComponentRequestParam,
  GetTextComponentResponse,
  GetTiffComponentRequestParam,
  GetTiffComponentResponse,
} from '../../api/types/production.type';

import {
  ExecuteGenerateResponse,
  ExecutePlaceholderingResponse,
  GetExportBatesRangeResponse,
} from '../../api/types/production.type';

export const useGetReProductLocationView = (
  params: GetReProductLocationViewRequestParam,
  enabled: boolean = false
) => {
  return useQuery<GetReProductLocationViewResponse, Error>({
    queryKey: ['getReProductLocationView', params],
    queryFn: () => getReProductLocationView(params),
    enabled,
  });
};

export const useSaveReProductionLocation = (
  onSuccess?: (data: SaveReProductionLocationResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    SaveReProductionLocationResponse,
    Error,
    SaveReProductionLocationRequestBody
  >({
    mutationFn: (body) => saveReProductionLocation(body),
    mutationKey: ['saveReProductionLocation'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetReProductionBasicInfoInEditMode = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<EditViewReProductionBasicInfoResponse, Error, string>({
    mutationFn: getReProductionBasicInfoInEditMode,
    mutationKey: ['getReProductionBasicInfoInEditMode'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetReProductionBasicInfo = (
  queryParams: GetReProductionBasicInfoRequestParam,
  enabled: boolean = true
) => {
  return useQuery<GetReProductionBasicInfoResponse, Error>({
    queryKey: ['getReProductionBasicInfo', queryParams],
    queryFn: () => getReProductionBasicInfo(queryParams),
    enabled,
  });
};

export const useSaveReProductionBasicInfo = (
  onSuccess?: (data: SaveReProductionBasicInfoResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    SaveReProductionBasicInfoResponse,
    Error,
    SaveReProductionBasicInfoPayload
  >({
    mutationFn: saveReProductionBasicInfo,
    mutationKey: ['saveReProductionBasicInfo'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useReProductions = (
  queryParams: GetPriorProductionListRequestParam,
  enabled: boolean
) => {
  return useQuery<GetPriorProductionListResponse, Error>({
    queryKey: ['getPriorProductionsList', queryParams],
    queryFn: () => getPriorProductionsList(queryParams),
    retry: 2,
    enabled,
  });
};

export const useSavePriorProductionToReProduce = (
  onSuccess?: (data: SavePriorProductionToReProduceResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    SavePriorProductionToReProduceResponse,
    Error,
    SavePriorProductionToReProduceRequestParam
  >({
    mutationFn: savePriorProductionToReProduce,
    mutationKey: ['savePriorProductionToReProduce'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useReProductionExecuteGenerate = (
  onSuccess?: (data: ExecuteGenerateResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    ExecuteGenerateResponse,
    Error,
    GetReProductionExecuteGenerateRequestBody
  >({
    mutationFn: (body) => reProductionExecuteGenerate(body),
    mutationKey: ['reProductionExecuteGenerate'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useReProductionNumbering = (
  queryParams: ReProductionNumberingRequestParam,
  enabled: boolean
) => {
  return useQuery<ReProductionNumberingResponse, Error>({
    queryKey: ['getReProductionNumbering', queryParams],
    queryFn: () => getReProductionNumbering(queryParams),
    retry: 2,
    enabled,
  });
};

export const useSaveReProductionNumbering = (
  onSuccess?: (data: SaveReProductionNumberingResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    SaveReProductionNumberingResponse,
    Error,
    SaveReProductionNumberingRequestParam
  >({
    mutationFn: saveReProductionNumbering,
    mutationKey: ['saveReProductionNumbering'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetReProductionGenerateView = (
  params: GetReProductionGenerateViewRequestParam,
  enabled: boolean = false
) => {
  return useQuery<GetReProductionGenerateViewResponse, Error>({
    queryKey: ['getReProductionGenerateView', params],
    queryFn: () => getReProductionGenerateView(params),
    enabled,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
};

export const usereProductionExecutePlaceholdering = (
  onSuccess?: (data: ExecutePlaceholderingResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    ExecutePlaceholderingResponse,
    Error,
    GetReProductionExecutePlaceholderingRequestBody
  >({
    mutationFn: (body) => reProductionExecutePlaceholdering(body),
    mutationKey: ['executePlaceholdering'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};
export const useGetReProductionDocumentSelectionView = (
  queryParams: GetDocumentSelectionViewRequestParam,
  enabled: boolean
) => {
  return useQuery<GetDocumentSelectionViewResponse, Error>({
    queryKey: ['getReProductionDocumentSelectionView', queryParams],
    queryFn: () => getReProductionDocumentSelectionView(queryParams),
    enabled,
    retry: 2,
  });
};

export const useGetReProductionDocumentsCount = (
  onSuccess?: (data: ReProductionDocumentsCountResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    ReProductionDocumentsCountResponse,
    Error,
    ReProductionDocumentsCountRequest
  >({
    mutationFn: getReProductionDocumentsCount,
    mutationKey: ['reProductionDocumentsCount'],
    onSuccess,
    onError,
  });
};

export const useSaveReProductionDocumentSelection = (
  onSuccess?: (data: SaveDocumentsSelectionResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    SaveDocumentsSelectionResponse,
    Error,
    SaveDocumentsSelectionPayload
  >({
    mutationFn: SaveReProductionDocumentSelection,
    mutationKey: ['SaveReProductionDocumentSelection'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetReProductionGenerateStatus = (
  params: getReProductionGenerateStatusParams,
  enabled: boolean = false,
  refetchInterval: number | false = false
) => {
  return useQuery<GetReProductionGenerateStatusResponse, Error>({
    queryKey: ['getReProductionGenerateStatus', params],
    queryFn: () => getReProductionGenerateStatus(params),
    enabled,
    refetchInterval,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
};

export const useGetReProductionExportBatesRange = (
  params: GetReProductionExportBatesRangeRequestParam,
  enabled: boolean = false
) => {
  return useQuery<GetExportBatesRangeResponse, Error>({
    queryKey: ['getReProductionExportBatesRange', params],
    queryFn: () => getReProductionExportBatesRange(params),
    enabled,
  });
};

export const useGetReProductionComponents = (
  queryParams: GetProductionComponentsRequestParam,
  enabled: boolean = true
) => {
  return useQuery<GetProductionComponentsResponse, Error>({
    queryKey: ['reProductionComponents', queryParams],
    queryFn: () => getReProductionComponents(queryParams),
    enabled,
    retry: 2,
  });
};

export const useSaveReProductionComponents = (
  onSuccess?: (data: SaveProductionComponentsResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    SaveProductionComponentsResponse,
    Error,
    SaveProductionComponentsPayload
  >({
    mutationFn: saveReProductionComponents,
    mutationKey: ['saveReProductionComponents'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetDatComponentReproduction = (
  queryParams: GetDatComponentRequestParam,
  enabled: boolean = true
) => {
  return useQuery<GetDatComponentResponse, Error>({
    queryKey: ['datComponentReproduction', queryParams],
    queryFn: () => getDatComponentReproduction(queryParams),
    enabled,
    retry: 2,
  });
};

export const useGetNativeComponentReproduction = (
  queryParams: GetNativeComponentRequestParam,
  enabled: boolean = true
) => {
  return useQuery<GetNativeComponentResponse, Error>({
    queryKey: ['nativeComponentReproduction', queryParams],
    queryFn: () => getNativeComponentReproduction(queryParams),
    enabled,
    retry: 2,
  });
};

export const useGetTextComponentReproduction = (
  queryParams: GetTextComponentRequestParam,
  enabled: boolean = true
) => {
  return useQuery<GetTextComponentResponse, Error>({
    queryKey: ['textComponentReproduction', queryParams],
    queryFn: () => getTextComponentReproduction(queryParams),
    enabled,
    retry: 2,
  });
};

export const useGetTiffComponentReproduction = (
  queryParams: GetTiffComponentRequestParam,
  enabled: boolean = true
) => {
  return useQuery<GetTiffComponentResponse, Error>({
    queryKey: ['tiffComponentReproduction', queryParams],
    queryFn: () => getTiffComponentReproduction(queryParams),
    enabled,
    retry: 2,
  });
};
